import type { RouteStop } from '../models/RouteStop';
import type { RouteDepartureState } from './routeDeparture';

export type RouteCollectionStatus = 'idle' | 'in_progress' | 'completed';

export type RouteCollectionState = RouteDepartureState & {
  collectionStatus?: RouteCollectionStatus | string | null;
  completedAt?: number | null;
  completedBy?: string | null;
  compositionStatus?: string | null;
  assignedRunnerId?: string | null;
};

export function isStopTerminal(stop: { status?: string | null }): boolean {
  const st = String(stop.status ?? '');
  return st === 'cleared' || st === 'skipped';
}

/** Pickup / ad-hoc stops — excludes the house HQ destination. */
export function countRoutePickupStops(
  stops: Array<{ kind?: string | null }> | null | undefined,
): number {
  if (!Array.isArray(stops)) return 0;
  return stops.filter((s) => String(s.kind ?? '') !== 'house').length;
}

/** A collection run is complete when every stop is cleared or skipped. */
export function isRouteCollectionComplete(
  stops: Array<{ status?: string | null }>,
): boolean {
  if (!Array.isArray(stops) || stops.length === 0) return false;
  return stops.every(isStopTerminal);
}

export function getRouteCollectionStatus(
  route: RouteCollectionState | null | undefined,
): RouteCollectionStatus {
  const raw = String(route?.collectionStatus ?? '').trim();
  if (raw === 'in_progress' || raw === 'completed' || raw === 'idle') return raw;
  // Explicit departure only — scheduled soft-unlock is not a started collection run.
  if (route?.departureStatus === 'authorized') return 'in_progress';
  if (typeof route?.authorizedAt === 'number' && route.authorizedAt > 0) return 'in_progress';
  return 'idle';
}

/**
 * Another departure may be authorized only when the previous run is finished
 * (or never started) and the route has at least one non-house stop.
 */
export function canAuthorizeDeparture(
  route: RouteCollectionState | null | undefined,
  stops?: Array<{ kind?: string | null }> | null,
): { ok: true } | { ok: false; reason: 'missing' | 'not_confirmed' | 'in_progress' | 'insufficient_stops' } {
  if (!route) return { ok: false, reason: 'missing' };
  const composition = String(route.compositionStatus ?? 'confirmed');
  if (composition === 'draft' || composition === 'none') {
    return { ok: false, reason: 'not_confirmed' };
  }
  if (getRouteCollectionStatus(route) === 'in_progress') {
    return { ok: false, reason: 'in_progress' };
  }
  const list = stops
    ?? (route as RouteCollectionState & { stops?: Array<{ kind?: string | null }> }).stops;
  if (countRoutePickupStops(list) < 1) {
    return { ok: false, reason: 'insufficient_stops' };
  }
  return { ok: true };
}

export function buildInProgressDeparturePatch(actorId: string, nowMs: number = Date.now()) {
  return {
    departureStatus: 'authorized' as const,
    authorizedAt: nowMs,
    authorizedBy: actorId,
    collectionStatus: 'in_progress' as const,
    completedAt: null,
    completedBy: null,
  };
}

export function buildCompletedCollectionPatch(
  actorId: string = 'system',
  nowMs: number = Date.now(),
  opts?: { manual?: boolean; reason?: string | null },
) {
  return {
    collectionStatus: 'completed' as const,
    completedAt: nowMs,
    completedBy: actorId,
    ...(opts?.manual
      ? {
        completedManually: true,
        completedReason: String(opts.reason ?? '').trim() || null,
      }
      : {}),
  };
}

export function shouldMarkRouteCompleted(
  route: RouteCollectionState | null | undefined,
  stops: RouteStop[] | Array<{ status?: string | null }>,
): boolean {
  if (getRouteCollectionStatus(route) !== 'in_progress') return false;
  return isRouteCollectionComplete(stops);
}

/** Banker/god may force-complete an in-progress collection run. */
export function canMarkRouteCompletedManually(
  route: RouteCollectionState | null | undefined,
): { ok: true } | { ok: false; reason: 'missing' | 'not_in_progress' } {
  if (!route) return { ok: false, reason: 'missing' };
  if (getRouteCollectionStatus(route) !== 'in_progress') {
    return { ok: false, reason: 'not_in_progress' };
  }
  return { ok: true };
}
